import React, { useMemo, lazy, Suspense } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { PanelRight, MessageSquare, History, Settings, X, Link2, Sparkles } from 'lucide-react';
import { ToolRegistry } from '../../core/tools/ToolRegistry';
import type { IToolContext } from '../../core/tools/interfaces/ITool';
import { useUIStore } from '../../stores/uiStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { useProcessViewStore } from '../../stores/processViewStore';
import { DiagramHistoryPanel } from '../panels/DiagramHistoryPanel';
import { CommentsSidePanel } from '../comments/CommentsSidePanel';
import { LinksPanel } from '../panels/LinksPanel';
import { getGravityUserId } from '../../hooks/useAuth';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ScrollArea } from '../ui/scroll-area';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { GenogramInsightPanel } from '../genogram/GenogramInsightPanel';
import { useGenogramAssistantUIStore } from '../../stores/genogramAssistantUIStore';
import type { Edge, Node } from '../../core/models/scene';
import {
  hasFloatingDiagramSettings,
  shouldShowFloatingDiagramSettingsButton,
  shouldShowFloatingLinksButton,
} from './floatingPropertiesVisibility';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { useCompassViewPrefs } from '../../stores/compassViewPrefsStore';

const isCompassConnectorEdge = (edge: Edge | null | undefined): boolean => {
  const attrs = (edge?.attrs ?? {}) as Record<string, unknown>;
  return (
    attrs.importedFrom === 'compass-explorer' ||
    attrs.layoutSource === 'compass-explorer' ||
    Boolean(attrs.infraEdgeKind || attrs.aggregated || attrs.expandedFrom)
  );
};

const nodeLabel = (node: Node | undefined, fallbackId: string | undefined): string => {
  const attrs = (node?.attrs ?? {}) as Record<string, unknown>;
  const label = attrs.title ?? attrs.label ?? attrs.name ?? node?.name ?? fallbackId;
  return String(label ?? '').replace(/^frame:/, '');
};

const titleCase = (value: unknown): string => {
  if (typeof value !== 'string' || value.trim().length === 0) return 'Connector';
  return value
    .replace(/[_-]/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

interface CompassConnectorPropertiesProps {
  edge: Edge;
  nodes: Node[];
}

const CompassConnectorProperties: React.FC<CompassConnectorPropertiesProps> = ({ edge, nodes }) => {
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  const nodeById = React.useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const fromNode = nodeById.get(edge.from);
  const toNode = nodeById.get(edge.to);
  const sourceContainer =
    typeof attrs.sourceContainerId === 'string' ? nodeById.get(attrs.sourceContainerId) : undefined;
  const targetContainer =
    typeof attrs.targetContainerId === 'string' ? nodeById.get(attrs.targetContainerId) : undefined;
  const aggregateCount = typeof attrs.aggregateCount === 'number'
    ? attrs.aggregateCount
    : Array.isArray(attrs.memberEdgeIds)
      ? attrs.memberEdgeIds.length
      : undefined;
  const memberKinds = Array.isArray(attrs.memberKinds)
    ? attrs.memberKinds.map((kind) => titleCase(kind)).join(', ')
    : undefined;
  const isExpandedMember = typeof attrs.expandedFrom === 'string';
  const canToggleDetails = Boolean(attrs.aggregated || isExpandedMember);
  const connectorTitle = attrs.aggregated
    ? 'Aggregated Data Flow'
    : isExpandedMember
      ? 'Expanded Data Flow'
      : titleCase(attrs.infraEdgeKind ?? edge.type);
  const toggleDetails = () => {
    const expandedFrom = typeof attrs.expandedFrom === 'string' ? attrs.expandedFrom : undefined;
    useCompassViewPrefs.getState().toggleExpandedEdge(expandedFrom ?? edge.id);
  };

  const rows: Array<[string, string | undefined]> = [
    ['From', nodeLabel(fromNode, edge.from)],
    ['To', nodeLabel(toNode, edge.to)],
    ['Source group', sourceContainer ? nodeLabel(sourceContainer, attrs.sourceContainerId as string) : undefined],
    ['Target group', targetContainer ? nodeLabel(targetContainer, attrs.targetContainerId as string) : undefined],
    ['Connections', aggregateCount && aggregateCount > 1 ? String(aggregateCount) : undefined],
    ['Kinds', memberKinds],
  ];

  return (
    <section className="px-4 py-4 border-b">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{connectorTitle}</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">
            Compass Explorer relationship
          </p>
        </div>
        {canToggleDetails ? (
          <button
            data-testid="connector-compass-properties-details"
            className="shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
            onClick={toggleDetails}
          >
            {isExpandedMember ? 'Collapse' : 'Details'}
          </button>
        ) : null}
      </div>
      <dl className="mt-4 space-y-3">
        {rows
          .filter(([, value]) => value && value.trim().length > 0)
          .map(([label, value]) => (
            <div key={label} className="min-w-0">
              <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {label}
              </dt>
              <dd className="mt-0.5 break-words text-sm text-foreground">
                {value}
              </dd>
            </div>
          ))}
      </dl>
    </section>
  );
};

const DiagramSettingsPanel = lazy(() =>
  import('../../domains/diagram').then((module) => ({
    default: module.DiagramSettingsPanel,
  }))
);

const OrgChartSettingsPanel = lazy(() =>
  import('../../domains/orgchart/components/OrgChartSettingsPanel').then((module) => ({
    default: module.OrgChartSettingsPanel,
  }))
);

const FlowchartSettingsPanel = lazy(() =>
  import('../../domains/process/components/FlowchartSettingsPanel').then((module) => ({
    default: module.FlowchartSettingsPanel,
  }))
);

const NetworkSettingsPanel = lazy(() =>
  import('../../domains/network/components/NetworkSettingsPanel').then((module) => ({
    default: module.NetworkSettingsPanel,
  }))
);

interface FloatingPropertiesProps {
  toolRegistry?: ToolRegistry;
  toolContext?: IToolContext;
  onToolExecute?: (toolId: string, params?: unknown) => void;
  offsetTop?: number;
  rightOffset?: string;
}

export const FloatingProperties: React.FC<FloatingPropertiesProps> = React.memo(({ toolRegistry, toolContext, onToolExecute, offsetTop = 24, rightOffset = '0px' }) => {
  const { t } = useTranslation('common');
  const { diagramType: docDiagramType } = useDiagramDocument();
  const [toolContextVersion, setToolContextVersion] = React.useState(0);
  const diagramId = React.useMemo(
    () => (window as Window & { __DIAGRAM_ID__?: string }).__DIAGRAM_ID__,
    [],
  );
  const userClosed = useUIStore(s => s.propertyPanelUserClosed);
  const setUserClosed = useUIStore(s => s.setPropertyPanelUserClosed);
  const activePanel = useUIStore(s => s.activePanel);
  const setActivePanel = useUIStore(s => s.setActivePanel);
  const diagramNodes = useDiagramStore(s => s.nodes);
  const diagramEdges = useDiagramStore(s => s.edges);

  React.useEffect(() => {
    if (!toolRegistry) return;
    const eventBus = toolRegistry.getContext().eventBus;
    const bump = () => setToolContextVersion((version) => version + 1);
    const offContextChanged = eventBus.on('tool:contextChanged', bump);
    // Domain plugins register their property tools asynchronously. A user can
    // select a shape before that registration completes, so context changes
    // alone are insufficient to make the newly available panel appear.
    const offToolRegistered = eventBus.on('tool:registered', bump);
    const offToolUnregistered = eventBus.on('tool:unregistered', bump);
    bump();
    return () => {
      offContextChanged?.();
      offToolRegistered?.();
      offToolUnregistered?.();
    };
  }, [toolRegistry]);
  const resolvedToolContext = React.useMemo(
    () => toolRegistry?.getContext() ?? toolContext,
    [toolRegistry, toolContext, toolContextVersion],
  );
  const diagramType = resolvedToolContext?.diagramType || docDiagramType;
  const isGenogramDiagram = diagramType === 'genogram';
  const insightPayload = useGenogramAssistantUIStore((s) => s.payload);
  const hasInsightPayload = Boolean(insightPayload && (insightPayload.title || insightPayload.description || insightPayload.toolName));
  const { isProcessRepositoryEnabled, loading: featureFlagsLoading } = useFeatureFlags();
  const showProcessRepository = !featureFlagsLoading && isProcessRepositoryEnabled();
  const hasDiagramSettings = hasFloatingDiagramSettings(diagramType);
  const showDiagramSettingsButton = shouldShowFloatingDiagramSettingsButton(diagramType, {
    processRepositoryEnabled: showProcessRepository,
  });
  const showLinksButton = shouldShowFloatingLinksButton(diagramType);
  const selectionSignature = useMemo(() => {
    const selection = resolvedToolContext?.selection;
    if (!selection || selection.size === 0) return '';
    // Stable, low-cost key so properties tools recompute when selected IDs change.
    return Array.from(selection).sort().join('|');
  }, [resolvedToolContext?.selection]);
  const selectionSize = resolvedToolContext?.selection?.size ?? 0;
  const prevSelectionSizeRef = React.useRef(selectionSize);
  const singleSelectedId = useMemo(() => {
    if (!selectionSignature || selectionSignature.includes('|')) return null;
    return selectionSignature;
  }, [selectionSignature]);
  const selectedCompassConnector = useMemo(() => {
    if (!singleSelectedId) return null;
    const edge = diagramEdges.find((candidate) => candidate.id === singleSelectedId);
    return edge && isCompassConnectorEdge(edge) ? edge : null;
  }, [diagramEdges, singleSelectedId]);

  // Recompute when the tool context refreshes; newly-created Yjs nodes can arrive after selection.
  const tools = useMemo(() => {
    if (!toolRegistry || !resolvedToolContext) {
      return [];
    }

    const allTools = toolRegistry.getToolsForLocation('propertyPanel', resolvedToolContext.selectionSummary);
    const toolsWithComponent = allTools.filter(t => typeof t.getPropertyPanelComponent === 'function');
    const visibleTools = toolsWithComponent.filter((tool) =>
      tool.canRenderPropertyPanel?.(resolvedToolContext) ?? tool.canExecute(resolvedToolContext)
    );

    return visibleTools;
  }, [toolRegistry, resolvedToolContext?.selectionSummary?.version, selectionSize, selectionSignature, toolContextVersion]);

  // Check if we have any content to render
  const hasCompassConnectorProperties = Boolean(selectedCompassConnector);
  const hasContent = React.useMemo(
    () => tools.length > 0 || hasCompassConnectorProperties,
    [tools, hasCompassConnectorProperties],
  );

  // Auto-close the properties panel if there's no content to render
  // (e.g., no selection → no tools expose a property panel).
  React.useEffect(() => {
    if (activePanel === 'properties' && !hasContent) {
      // This is an implicit close (not user-initiated)
      setActivePanel(null);
      // Do not mark userClosed so we can auto-open when content returns
    }
  }, [activePanel, hasContent, setActivePanel]);

  // Auto-open the properties panel when relevant content appears again,
  // but only if the user did not explicitly close it.
  React.useEffect(() => {
    if (activePanel === null && !userClosed && hasContent) {
      setActivePanel('properties');
    }
  }, [activePanel, userClosed, hasContent, setActivePanel]);

  React.useEffect(() => {
    if (activePanel !== 'diagram-settings') return;
    if (showDiagramSettingsButton) return;
    setActivePanel(null);
  }, [activePanel, showDiagramSettingsButton, setActivePanel]);

  // Clear selection when switching to diagram settings for genograms, since the settings are not contextual and can be confusing with a selection
  React.useEffect(() => {
    if (activePanel !== 'diagram-settings') return;
    if (diagramType !== 'genogram') return;
    const state = useDiagramStore.getState();
    if ((state.selectedIds?.size ?? 0) === 0) return;
    state.setSelected(new Set());
  }, [activePanel, diagramType]);

  // Auto-switch to properties panel only when a new selection appears while diagram settings is already open
  React.useEffect(() => {
    if (activePanel !== 'diagram-settings') {
      prevSelectionSizeRef.current = selectionSize;
      return;
    }

    const previousSize = prevSelectionSizeRef.current;
    prevSelectionSizeRef.current = selectionSize;

    const hasNewSelection = previousSize === 0 && selectionSize > 0;
    if (!hasNewSelection) return;
    if (!hasContent) return;
    setActivePanel('properties');
  }, [activePanel, hasContent, selectionSize, setActivePanel]);

  React.useEffect(() => {
    if (activePanel !== 'used-in') return;
    if (showLinksButton) return;
    setActivePanel(null);
  }, [activePanel, showLinksButton, setActivePanel]);

  // Don't render tools when there's no selection, but still allow other panels
  const canRenderProperties = toolRegistry && resolvedToolContext && (resolvedToolContext.selection?.size ?? 0) > 0;

  const renderAllTools = () => {
    if (!resolvedToolContext) return null;
    const sortedTools = [...tools].sort((a, b) => {
      if (a.manifest.id === 'shape-notes') return 1;
      if (b.manifest.id === 'shape-notes') return -1;
      return 0;
    });

    return sortedTools.map((t) => {
      const C = t.getPropertyPanelComponent?.();
      if (!C) return null;
      return (
        <div key={t.manifest.id}>
          <C tool={t} context={resolvedToolContext!} onExecute={(params: unknown) => onToolExecute?.(t.manifest.id, params)} />
        </div>
      );
    });
  };

  const hasOrgDataTool = useMemo(() => tools.some(t => t.manifest.id === 'orgchart-data'), [tools]);
  const hasProcessTool = useMemo(() => tools.some(t => t.manifest.id === 'process-basic-properties'), [tools]);
  const processEditMode = useProcessViewStore((s) => s.propertyPanelEditMode);

  // Check if relationship tool is active (for wider panel)
  const hasRelationshipTool = useMemo(() => {
    if (!tools || tools.length === 0) return false;
    return tools.some(t => t.manifest.id === 'genogram-relationship-edit' || t.manifest.id === 'genogram-connector-edit');
  }, [tools]);

  // Determine panel width class based on active panel
  const panelWidth = useMemo(() => {
    if (activePanel === 'diagram-settings') {
      return 'w-[420px]';
    } else if (activePanel === 'assistant-insights') {
      return 'w-[420px]';
    } else if (activePanel === 'diagram-history') {
      return 'w-[320px]';
    } else if (activePanel === 'used-in') {
      return 'w-[320px]';
    } else if (activePanel === 'properties') {
      if (hasRelationshipTool) return 'w-[446px]';
      if (hasCompassConnectorProperties) return 'w-[360px]';
      if (hasOrgDataTool) return 'w-[320px]';
      if (hasProcessTool) return processEditMode ? 'w-[560px]' : 'w-[420px]';
      return 'w-[280px]';
    }
    return 'w-[280px]';
  }, [activePanel, hasRelationshipTool, hasCompassConnectorProperties, hasOrgDataTool, hasProcessTool, processEditMode]);

  // Get numeric panel width for toolbar positioning
  const panelWidthPx = useMemo(() => {
    if (activePanel === 'diagram-settings') {
      return 420;
    } else if (activePanel === 'assistant-insights') {
      return 420;
    } else if (activePanel === 'diagram-history') {
      return 320;
    } else if (activePanel === 'used-in') {
      return 320;
    } else if (activePanel === 'properties') {
      // Only use wider width for relationship tools
      if (hasRelationshipTool) return 446;
      if (hasCompassConnectorProperties) return 360;
      if (hasOrgDataTool) return 320;
      if (hasProcessTool) return processEditMode ? 560 : 420;
      return 280;
    } else if (activePanel === 'comments') {
      return 280;
    }
    return 0;
  }, [activePanel, hasRelationshipTool, hasCompassConnectorProperties, hasOrgDataTool, hasProcessTool, processEditMode]);

  const RAIL_RIGHT_OFFSET_PX = 24;
  const railRight = activePanel !== null
    ? rightOffset === '0px'
      ? `${panelWidthPx + RAIL_RIGHT_OFFSET_PX}px`
      : `calc(${rightOffset} + ${panelWidthPx + RAIL_RIGHT_OFFSET_PX}px)`
    : rightOffset === '0px'
      ? '1.5rem'
      : `calc(${rightOffset} + 1.5rem)`;

  const dockedPanelClassName = `${panelWidth} h-full max-h-full min-h-0 flex flex-col bg-background border-l shadow-lg pointer-events-auto animate-in slide-in-from-right duration-300`;

  const currentUserId = useMemo(() => {
    const fromStore = getGravityUserId();
    return fromStore ?? 'anonymous';
  }, []);

  const isFlowchartDiagram = diagramType === 'flowchart';
  const propertiesLabel = isFlowchartDiagram
    ? t('floatingProperties.stepProperties', { ns: 'diagram' })
    : t('floatingProperties.properties', { ns: 'diagram' });
  const propertiesDisabledHint = t('floatingProperties.selectStepHint', { ns: 'diagram' });
  const propertiesTooltip =
    !canRenderProperties && isFlowchartDiagram ? propertiesDisabledHint : propertiesLabel;
  const propertiesPanelTitle = hasRelationshipTool
    ? t('panels.relationshipsLibrary', { ns: 'diagram' })
    : isFlowchartDiagram
      ? t('floatingProperties.stepProperties', { ns: 'diagram' })
      : t('panels.properties');
  const commentsLabel = t('floatingProperties.comments', { ns: 'diagram' });
  const historyLabel = t('floatingProperties.history', { ns: 'diagram' });
  const linksLabel = t('floatingProperties.links', { ns: 'diagram', defaultValue: 'Links' });

  return (
    <>
      {/* Root overlay shouldn't block canvas when no panel is open */}
      <div
        className="absolute top-0 right-0 bottom-0 z-[50] pointer-events-none select-none flex h-full min-h-0 flex-col items-end"
        style={{ right: rightOffset }}
      >
        {/* Floating Icon Toolbar - matches bottom toolbar style */}
        <TooltipProvider skipDelayDuration={0} disableHoverableContent>
          <div
            data-testid="tid.floating-properties.rail"
            className="
              fixed z-[1001]
              bg-white dark:bg-gray-900 rounded-[24px]
              border border-gray-200 dark:border-gray-700
              shadow-[0_4px_12px_rgba(0,0,0,0.1)]
              flex items-center gap-1 p-1.5
              pointer-events-auto
            "
            style={{
              top: offsetTop,
              right: railRight,
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="tid.properties.toggle"
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    transition-colors
                    ${activePanel === 'properties' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                    ${!canRenderProperties ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                  onClick={() => {
                    if (!canRenderProperties) return;
                    if (activePanel === 'properties') {
                      setActivePanel(null);
                      setUserClosed(true);
                    } else {
                      setActivePanel('properties');
                      setUserClosed(false);
                    }
                  }}
                  aria-label={propertiesTooltip}
                  title={propertiesTooltip}
                  disabled={!canRenderProperties}
                >
                  <PanelRight className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent key={propertiesTooltip}>
                {propertiesTooltip}
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="tid.editor.rail.comments"
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    transition-colors
                    ${activePanel === 'comments' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                  `}
                  onClick={() => {
                    if (activePanel === 'comments') {
                      setActivePanel(null);
                      setUserClosed(true);
                    } else {
                      setActivePanel('comments');
                      setUserClosed(false);
                    }
                  }}
                  aria-label={commentsLabel}
                >
                  <MessageSquare className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent key={commentsLabel}>
                {commentsLabel}
              </TooltipContent>
            </Tooltip>

            {isGenogramDiagram && hasInsightPayload ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    data-testid="tid.editor.rail.insights"
                    className={`
                      w-10 h-10 rounded-lg flex items-center justify-center
                      transition-colors
                      ${activePanel === 'assistant-insights' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                    `}
                    onClick={() => {
                      if (activePanel === 'assistant-insights') {
                        setActivePanel(null);
                        setUserClosed(true);
                      } else {
                        setActivePanel('assistant-insights');
                        setUserClosed(false);
                      }
                    }}
                    aria-label={t('panels.assistantInsights', { ns: 'diagram' })}
                  >
                    <Sparkles className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent key={t('panels.assistantInsights', { ns: 'diagram' })}>
                  {t('panels.assistantInsights', { ns: 'diagram' })}
                </TooltipContent>
              </Tooltip>
            ) : null}

            {showDiagramSettingsButton ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={!hasDiagramSettings ? 'opacity-50 cursor-not-allowed' : undefined}>
                  <button
                    data-testid="tid.editor.rail.settings"
                    className={`
                      w-10 h-10 rounded-lg flex items-center justify-center
                      transition-colors
                      ${activePanel === 'diagram-settings' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                        ${!hasDiagramSettings ? 'cursor-not-allowed' : ''}
                    `}
                    onClick={() => {
                        if (!hasDiagramSettings) return;
                      if (activePanel === 'diagram-settings') {
                        setActivePanel(null);
                        setUserClosed(true);
                      } else {
                        setActivePanel('diagram-settings');
                        setUserClosed(false);
                      }
                    }}
                    aria-label={t('panels.diagramSettings')}
                      disabled={!hasDiagramSettings}
                  >
                    <Settings className="h-5 w-5" />
                  </button>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  key={
                    hasDiagramSettings
                      ? t('floatingProperties.diagramSettings', { ns: 'diagram' })
                      : t('floatingProperties.noDiagramSettings', {
                          ns: 'diagram',
                          defaultValue: 'No settings for this diagram type',
                        })
                  }
                >
                  {hasDiagramSettings
                    ? t('floatingProperties.diagramSettings', { ns: 'diagram' })
                    : t('floatingProperties.noDiagramSettings', {
                        ns: 'diagram',
                        defaultValue: 'No settings for this diagram type',
                      })}
                </TooltipContent>
              </Tooltip>
            ) : null}

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  data-testid="tid.editor.rail.history"
                  className={`
                    w-10 h-10 rounded-lg flex items-center justify-center
                    transition-colors
                    ${activePanel === 'diagram-history' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                  `}
                  onClick={() => {
                    if (activePanel === 'diagram-history') {
                      setActivePanel(null);
                      setUserClosed(true);
                    } else {
                      setActivePanel('diagram-history');
                      setUserClosed(false);
                    }
                  }}
                  aria-label={historyLabel}
                >
                  <History className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent key={historyLabel}>
                {historyLabel}
              </TooltipContent>
            </Tooltip>

            {showLinksButton ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                      data-testid="tid.editor.rail.links"
                      className={`
                        w-10 h-10 rounded-lg flex items-center justify-center
                        transition-colors
                        ${activePanel === 'used-in' ? 'bg-gray-100 dark:bg-gray-800' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}
                      `}
                      onClick={() => {
                        if (activePanel === 'used-in') {
                          setActivePanel(null);
                          setUserClosed(true);
                        } else {
                          setActivePanel('used-in');
                          setUserClosed(false);
                        }
                      }}
                      aria-label={linksLabel}
                    >
                      <Link2 className="h-5 w-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent key={linksLabel}>
                  {linksLabel}
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
        </TooltipProvider>

        {/* Properties panel */}
        {activePanel === 'properties' && canRenderProperties && panelWidthPx > 0 && (
          <div
            data-testid="tid.properties.panel"
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">
                {propertiesPanelTitle}
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.properties.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <div className="h-0 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                <div className="pb-24 min-w-0 w-full">
                  {selectedCompassConnector ? (
                    <CompassConnectorProperties edge={selectedCompassConnector} nodes={diagramNodes} />
                  ) : null}
                  {renderAllTools()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comments panel */}
        {activePanel === 'comments' && panelWidthPx > 0 && (
          <div
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">{t('panels.comments')}</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.editor.panel.comments.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <CommentsSidePanel diagramId={diagramId} currentUserId={currentUserId} />
            </div>
          </div>
        )}

        {/* Diagram Settings panel */}
        {activePanel === 'diagram-settings' && showDiagramSettingsButton && hasDiagramSettings && diagramType && panelWidthPx > 0 && (
          <div
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">{t('panels.diagramSettings')}</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.editor.panel.settings.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <Suspense fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="text-sm text-muted-foreground">{t('status.loading', 'Loading...')}</div>
                </div>
              }>
                {diagramType === 'org' ? (
                  <OrgChartSettingsPanel hideTitle />
                ) : diagramType === 'flowchart' ? (
                  <FlowchartSettingsPanel hideTitle />
                ) : diagramType === 'network' ? (
                  <NetworkSettingsPanel hideTitle />
                ) : (
                  <DiagramSettingsPanel diagramType={diagramType ?? 'genogram'} hideTitle />
                )}
              </Suspense>
            </div>
          </div>
        )}

        {activePanel === 'diagram-history' && panelWidthPx > 0 && (
          <div
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">{t('panels.history')}</h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.editor.panel.history.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden p-4">
              <DiagramHistoryPanel diagramId={diagramId} />
            </div>
          </div>
        )}

        {activePanel === 'used-in' && showLinksButton && panelWidthPx > 0 && (
          <div
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">
                {t('panels.links', { ns: 'diagram', defaultValue: 'Links' })}
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.editor.panel.links.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <ScrollArea className="h-full">
                {diagramId ? (
                  <LinksPanel diagramId={diagramId} toolRegistry={toolRegistry} toolContext={toolContext} onToolExecute={onToolExecute} />
                ) : (
                  <div className="px-4 py-3 text-sm text-muted-foreground">
                    {t('status.loading', { ns: 'diagram', defaultValue: 'Loading...' })}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}

        {activePanel === 'assistant-insights' && isGenogramDiagram && panelWidthPx > 0 && (
          <div
            className={dockedPanelClassName}
            style={{ animationFillMode: 'both' }}
          >
            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-semibold text-foreground">
                {t('panels.assistantInsights', { ns: 'diagram' })}
              </h2>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      data-testid="tid.editor.panel.insights.close"
                      onClick={() => {
                        setActivePanel(null);
                        setUserClosed(true);
                      }}
                      className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      aria-label={t('actions.close')}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{t('actions.close')}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex-1 min-h-0 overflow-hidden">
              <GenogramInsightPanel />
            </div>
          </div>
        )}
       </div>
     </>
   );
 });

FloatingProperties.displayName = 'FloatingProperties';
